import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Stethoscope, HelpCircle, ListChecks, ArrowRight, FileText, AlertTriangle,
  UserPlus, Check, Clock, Loader2, CheckCircle2, Circle, MessageSquareText, Send, CalendarDays, Printer, Video, Star,
} from "lucide-react";
import { CareShell } from "@/components/CareShell";
import { RecordPreviewDialog } from "@/components/RecordPreviewDialog";
import { BookingSlots, slotRequired } from "@/components/BookingSlots";
import api, { apiErrorMessage } from "@/lib/api";
import { markCaseSeen } from "@/lib/careNotify";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} className="text-[#EF9F27]" />
        <h2 className="font-head text-lg font-bold text-[#F8FAFC]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

const STAGES = [
  { key: "submitted", label: "Submitted", desc: "We received your case and records." },
  { key: "under_review", label: "Specialist reviewing", desc: "A specialist is reviewing your concern." },
  { key: "opinion_ready", label: "Opinion ready", desc: "Your written second opinion is available." },
];

function Timeline({ status }) {
  const idx = status === "opinion_ready" ? 2 : 1;
  return (
    <ol className="mt-2" data-testid="case-timeline">
      {STAGES.map((s, i) => {
        const done = i < idx || (i === 2 && status === "opinion_ready");
        const active = i === idx && status !== "opinion_ready";
        const StageIcon = done ? CheckCircle2 : active ? Loader2 : Circle;
        return (
          <li key={s.key} className="flex gap-3" data-testid={`case-stage-${s.key}`}>
            <div className="flex flex-col items-center">
              <StageIcon
                size={22}
                className={
                  done ? "text-[#22C55E]" : active ? "text-[#EF9F27] animate-spin" : "text-[#555555]"
                }
              />
              {i < STAGES.length - 1 && (
                <span className={`w-0.5 flex-1 my-1 ${done ? "bg-[#22C55E]" : "bg-[#333333]"}`} />
              )}
            </div>
            <div className={`pb-5 ${i === STAGES.length - 1 ? "pb-0" : ""}`}>
              <p className={`font-semibold ${done || active ? "text-[#F8FAFC]" : "text-[#777777]"}`}>
                {s.label}
              </p>
              <p className="text-sm text-[#A1A1AA]">{s.desc}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function CaseDetail() {
  const { caseId } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [showDoctors, setShowDoctors] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [bookingDoctor, setBookingDoctor] = useState(null);
  const [bookDate, setBookDate] = useState("");
  const [bookTime, setBookTime] = useState("");
  const [availability, setAvailability] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [videoAvail, setVideoAvail] = useState([]);
  const [loadingVideoAvail, setLoadingVideoAvail] = useState(false);
  const [vDate, setVDate] = useState("");
  const [vTime, setVTime] = useState("");
  const [videoRequesting, setVideoRequesting] = useState(false);
  const [rescheduleLocal, setRescheduleLocal] = useState(false);
  const [rescheduleVideo, setRescheduleVideo] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingBusy, setRatingBusy] = useState(false);

  const submitRating = async () => {
    if (!ratingStars) return toast.error("Please select a star rating.");
    setRatingBusy(true);
    try {
      await api.post(`/care/cases/${caseId}/rating`, { stars: ratingStars, comment: ratingComment });
      toast.success("Thanks for rating your specialist.");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setRatingBusy(false);
    }
  };

  const load = () =>
    api.get(`/care/cases/${caseId}`).then(({ data }) => {
      setC(data);
      if (data?.status === "opinion_ready") markCaseSeen(data.id);
    }).finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/care/profile").then(({ data }) => {
      if (data?.country) {
        api.get(`/care/doctors?country=${encodeURIComponent(data.country)}`).then((r) => setDoctors(r.data || []));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // While under review: poll for readiness + tick a countdown.
  useEffect(() => {
    if (c && c.status !== "opinion_ready") {
      const poll = setInterval(load, 4000);
      const tick = setInterval(() => setNowMs(Date.now()), 1000);
      return () => {
        clearInterval(poll);
        clearInterval(tick);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.status]);

  const chooseDoctor = async (d) => {
    setBookingDoctor(d);
    setBookDate("");
    setBookTime("");
    setAvailability([]);
    setLoadingAvail(true);
    try {
      const { data } = await api.get(`/care/doctors/${d.id}/availability`);
      setAvailability(data.availability || []);
    } catch {
      setAvailability([]);
    } finally {
      setLoadingAvail(false);
    }
  };

  const requestConsult = async () => {
    if (!bookingDoctor) return;
    if (!bookDate) {
      toast.error("Please choose a preferred date.");
      return;
    }
    setRequesting(true);
    try {
      const { data } = await api.post(`/care/cases/${caseId}/consultation`, {
        doctor_id: bookingDoctor.id,
        preferred_date: bookDate,
        preferred_time: bookTime,
      });
      setC((prev) => ({ ...prev, consultation: data.consultation }));
      setShowDoctors(false);
      setBookingDoctor(null);
      setRescheduleLocal(false);
      toast.success("Consultation requested.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setRequesting(false);
    }
  };

  const sendMessage = async () => {
    const text = msgText.trim();
    if (!text) return;
    setSending(true);
    try {
      const { data } = await api.post(`/care/cases/${caseId}/messages`, { text });
      setC((prev) => ({ ...prev, messages: data.messages }));
      setMsgText("");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const openVideoBooking = async () => {
    setShowVideo(true);
    const spId = c?.opinion?.specialist?.id;
    if (!spId) return;
    setLoadingVideoAvail(true);
    try {
      const { data } = await api.get(`/care/specialists/${spId}/availability`);
      setVideoAvail(data.availability || []);
    } catch {
      setVideoAvail([]);
    } finally {
      setLoadingVideoAvail(false);
    }
  };

  const scheduleVideo = async () => {
    if (!vDate) {
      toast.error("Please choose a date.");
      return;
    }
    setVideoRequesting(true);
    try {
      const { data } = await api.post(`/care/cases/${caseId}/video-consultation`, {
        date: vDate,
        time: vTime,
      });
      setC((prev) => ({ ...prev, video_consultation: data.video_consultation }));
      setShowVideo(false);
      setRescheduleVideo(false);
      toast.success("Video review scheduled.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setVideoRequesting(false);
    }
  };

  const cancelConsult = async () => {
    try {
      await api.delete(`/care/cases/${caseId}/consultation`);
      setC((prev) => ({ ...prev, consultation: null }));
      setRescheduleLocal(false);
      setBookingDoctor(null);
      setShowDoctors(false);
      toast.success("Consultation cancelled.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const cancelVideo = async () => {
    try {
      await api.delete(`/care/cases/${caseId}/video-consultation`);
      setC((prev) => ({ ...prev, video_consultation: null }));
      setRescheduleVideo(false);
      setShowVideo(false);
      toast.success("Video review cancelled.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const startRescheduleLocal = () => {
    setRescheduleLocal(true);
    setShowDoctors(true);
    if (c.consultation?.doctor) chooseDoctor(c.consultation.doctor);
  };

  const startRescheduleVideo = () => {
    setRescheduleVideo(true);
    openVideoBooking();
  };

  if (loading) {
    return (
      <CareShell active="cases" title="Case" showBack>
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 bg-[#1A1A1A] border border-[#333333] rounded-2xl animate-pulse" />
          ))}
        </div>
      </CareShell>
    );
  }

  if (!c) {
    return (
      <CareShell active="cases" title="Case" showBack>
        <p className="text-[#A1A1AA]">Case not found.</p>
      </CareShell>
    );
  }

  const isReady = c.status === "opinion_ready";
  const op = c.opinion || {};
  const sp = op.specialist || {};
  const remaining = c.ready_at
    ? Math.max(0, Math.ceil((new Date(c.ready_at).getTime() - nowMs) / 1000))
    : 0;

  return (
    <CareShell active="cases" title={c.title} showBack>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="bg-[#EF9F27]/20 text-[#EF9F27] px-3 py-1 rounded-full text-xs font-bold tracking-wide">
          {c.specialty}
        </span>
        <span className="text-xs text-[#666666]">
          {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
        </span>
      </div>

      <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5" data-testid="case-concern">
        <p className="text-xs font-bold tracking-widest uppercase text-[#A1A1AA] mb-1">Your concern</p>
        <p className="text-[#F8FAFC] leading-relaxed">{c.concern}</p>
      </div>

      {!isReady ? (
        <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5" data-testid="case-review-status">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={18} className="text-[#EF9F27]" />
            <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Under review</h2>
          </div>
          <p className="text-sm text-[#A1A1AA] mb-4">
            A verified specialist is reviewing your case. You'll be notified as soon as their written
            opinion is ready — this usually takes a little time.
          </p>
          <Timeline status={c.status} />
        </div>
      ) : (
        <>
          {/* Specialist */}
          <div className="mt-4 bg-[#222222] border border-[#333333] rounded-2xl p-5" data-testid="case-specialist">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#EF9F27] text-[#111111] flex items-center justify-center font-head font-extrabold">
                {(sp.name || "Dr").replace(/[^A-Za-z ]/g, "").trim().split(" ").filter(Boolean).slice(-1)[0]?.[0] || "D"}
              </div>
              <div>
                <p className="font-head font-bold text-[#F8FAFC]">{sp.name}</p>
                <p className="text-sm text-[#EF9F27]">{sp.specialty}</p>
                <p className="text-xs text-[#A1A1AA]">{sp.based}</p>
              </div>
            </div>
          </div>

          <h2 className="font-head text-xl font-bold text-[#F8FAFC] mt-6" data-testid="case-opinion-heading">
            Second opinion
          </h2>

          <Section icon={Stethoscope} title="Summary">
            <p className="text-[#E5E5E5] leading-relaxed">{op.summary}</p>
          </Section>

          <Section icon={ListChecks} title="Key considerations">
            <ul className="space-y-2">
              {(op.considerations || []).map((x, i) => (
                <li key={i} className="flex gap-2 text-[#E5E5E5]">
                  <span className="text-[#EF9F27] mt-1">•</span> <span>{x}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section icon={HelpCircle} title="Questions to ask your doctor">
            <ul className="space-y-2">
              {(op.suggested_questions || []).map((x, i) => (
                <li key={i} className="flex gap-2 text-[#E5E5E5]">
                  <span className="text-[#EF9F27] mt-1">•</span> <span>{x}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section icon={ArrowRight} title="Next steps">
            <p className="text-[#E5E5E5] leading-relaxed">{op.next_steps}</p>
          </Section>

          {c.documents && c.documents.length > 0 && (
            <Section icon={FileText} title="Attached records">
              <ul className="space-y-2">
                {c.documents.map((d) => (
                  <li key={d.id}>
                    <button
                      data-testid={`case-record-${d.id}`}
                      onClick={() => setPreviewDoc(d.id)}
                      className="w-full text-left flex items-center gap-2 text-[#E5E5E5] rounded-lg px-2 py-1.5 hover:bg-[#222222] transition-colors"
                    >
                      <FileText size={16} className="text-[#A1A1AA] shrink-0" />
                      <span className="truncate">{d.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Share for local doctor */}
          <button
            data-testid="case-share-btn"
            onClick={() => window.print()}
            className="mt-4 w-full rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-6 py-3.5 min-h-[48px] flex items-center justify-center gap-2 hover:bg-[#333333] transition-colors"
          >
            <Printer size={18} /> Share with my doctor (PDF)
          </button>

          {/* Disclaimer */}
          <div
            className="mt-4 bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-xl p-4 flex gap-3"
            data-testid="case-disclaimer"
          >
            <AlertTriangle size={18} className="text-[#EF9F27] shrink-0 mt-0.5" />
            <p className="text-sm text-[#EFC77A] leading-relaxed">{op.disclaimer}</p>
          </div>

          {/* Rate the specialist (only for real specialist opinions) */}
          {c.opinion_source === "specialist" && (
            <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5" data-testid="case-rating">
              {c.rating ? (
                <div data-testid="case-rating-done">
                  <p className="text-sm text-[#A1A1AA]">You rated this opinion</p>
                  <div className="flex items-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={20} className={n <= c.rating.stars ? "text-[#EF9F27] fill-[#EF9F27]" : "text-[#444444]"} />
                    ))}
                  </div>
                  {c.rating.comment && <p className="text-sm text-[#E5E5E5] mt-2 italic">"{c.rating.comment}"</p>}
                </div>
              ) : (
                <>
                  <p className="font-head font-bold text-[#F8FAFC]">Rate your second opinion</p>
                  <p className="text-sm text-[#A1A1AA] mt-1">How helpful was {sp.name || "your specialist"}'s review?</p>
                  <div className="flex items-center gap-2 mt-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} data-testid={`case-rating-star-${n}`} onClick={() => setRatingStars(n)} aria-label={`${n} star`}
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center">
                        <Star size={28} className={n <= ratingStars ? "text-[#EF9F27] fill-[#EF9F27]" : "text-[#555555]"} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    data-testid="case-rating-comment"
                    rows={2}
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    placeholder="Optional: a few words about your experience"
                    className="mt-3 w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3 text-[#F8FAFC] placeholder:text-[#666666] focus:outline-none focus:border-[#EF9F27]"
                  />
                  <button data-testid="case-rating-submit" onClick={submitRating} disabled={ratingBusy || !ratingStars}
                    className="mt-3 w-full rounded-full bg-[#EF9F27] text-[#111111] font-bold px-4 py-3 min-h-[48px] disabled:opacity-50">
                    {ratingBusy ? "Submitting…" : "Submit rating"}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Consultation */}
          <div className="mt-6">
            {c.consultation && !rescheduleLocal ? (
              <div className="bg-[#16A34A]/10 border border-[#16A34A]/30 rounded-2xl p-5" data-testid="case-consultation">
                <div className="flex items-center gap-2">
                  <Check size={18} className="text-[#22C55E]" />
                  <p className="font-head font-bold text-[#F8FAFC]">Consultation requested</p>
                </div>
                <p className="text-sm text-[#A1A1AA] mt-1">
                  With {c.consultation.doctor?.name} · {c.consultation.doctor?.clinic}, {c.consultation.doctor?.city}
                </p>
                {(c.consultation.preferred_date || c.consultation.preferred_time) && (
                  <p className="text-sm text-[#F8FAFC] mt-2 inline-flex items-center gap-2" data-testid="case-consultation-when">
                    <CalendarDays size={15} className="text-[#22C55E]" />
                    {c.consultation.preferred_date}
                    {c.consultation.preferred_time ? ` at ${c.consultation.preferred_time}` : ""}
                  </p>
                )}
                <div className="mt-4 flex gap-3">
                  <button
                    data-testid="consult-reschedule-btn"
                    onClick={startRescheduleLocal}
                    className="flex-1 rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-4 py-2.5 min-h-[44px]"
                  >
                    Reschedule
                  </button>
                  <button
                    data-testid="consult-cancel-btn"
                    onClick={cancelConsult}
                    className="flex-1 rounded-full border border-[#EF4444]/50 text-[#EF4444] font-bold px-4 py-2.5 min-h-[44px] hover:bg-[#EF4444]/10 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {!rescheduleLocal && (
                  <button
                    data-testid="case-request-consult-btn"
                    onClick={() => setShowDoctors((s) => !s)}
                    className="w-full rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-6 py-3.5 min-h-[48px] flex items-center justify-center gap-2 hover:bg-[#333333] transition-colors"
                  >
                    <UserPlus size={18} /> Book a local consultation
                  </button>
                )}
                {rescheduleLocal && (
                  <p className="font-head font-bold text-[#F8FAFC] mb-1">Reschedule consultation</p>
                )}
                {showDoctors && !bookingDoctor && (
                  <ul className="mt-3 space-y-3" data-testid="case-doctors-list">
                    {doctors.length === 0 ? (
                      <li className="text-sm text-[#A1A1AA] bg-[#1A1A1A] border border-[#333333] rounded-xl p-4">
                        No local doctors listed for your country yet.
                      </li>
                    ) : (
                      doctors.map((d) => (
                        <li key={d.id}>
                          <button
                            data-testid={`case-doctor-${d.id}`}
                            onClick={() => chooseDoctor(d)}
                            className="w-full text-left bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4 hover:bg-[#222222] transition-colors"
                          >
                            <p className="font-head font-bold text-[#F8FAFC]">{d.name}</p>
                            <p className="text-sm text-[#EF9F27]">{d.specialty}</p>
                            <p className="text-xs text-[#A1A1AA] mt-0.5">
                              {d.clinic}, {d.city} · {d.languages.join(", ")}
                            </p>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
                {bookingDoctor && (
                  <div className="mt-3 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5" data-testid="case-booking-form">
                    <p className="font-head font-bold text-[#F8FAFC]">{bookingDoctor.name}</p>
                    <p className="text-sm text-[#EF9F27]">
                      {bookingDoctor.clinic}, {bookingDoctor.city}
                    </p>

                    {loadingAvail ? (
                      <div className="h-24 flex items-center justify-center">
                        <Loader2 className="animate-spin text-[#EF9F27]" />
                      </div>
                    ) : availability.length === 0 ? (
                      <p className="mt-4 text-sm text-[#A1A1AA] bg-[#111111] border border-[#333333] rounded-xl p-4">
                        No open slots in the next few weeks. Please check back soon.
                      </p>
                    ) : (
                      <>
                        <p className="mt-4 text-xs text-[#A1A1AA] inline-flex items-center gap-1.5">
                          <CalendarDays size={13} className="text-[#EF9F27]" /> Showing this doctor's open slots
                        </p>
                        <label className="block text-sm font-medium text-[#A1A1AA] mt-3 mb-1.5">Available date</label>
                        <select
                          data-testid="booking-date-input"
                          value={bookDate}
                          onChange={(e) => {
                            setBookDate(e.target.value);
                            setBookTime("");
                          }}
                          className="w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] focus:outline-none focus:border-[#EF9F27]"
                        >
                          <option value="">Choose a date</option>
                          {availability.map((a) => (
                            <option key={a.date} value={a.date}>
                              {new Date(a.date + "T00:00:00").toLocaleDateString(undefined, {
                                weekday: "short", day: "numeric", month: "short",
                              })}
                            </option>
                          ))}
                        </select>

                        {bookDate && (
                          <>
                            <label className="block text-sm font-medium text-[#A1A1AA] mt-3 mb-1.5">Available time</label>
                            <div className="grid grid-cols-4 gap-2" data-testid="booking-time-slots">
                              {(availability.find((a) => a.date === bookDate)?.slots || []).map((t) => (
                                <button
                                  key={t}
                                  data-testid={`booking-slot-${t}`}
                                  onClick={() => setBookTime(t)}
                                  className={`min-h-[44px] rounded-xl text-sm font-bold border transition-colors ${
                                    bookTime === t
                                      ? "bg-[#EF9F27] text-[#111111] border-[#EF9F27]"
                                      : "bg-[#111111] text-[#F8FAFC] border-[#333333] hover:border-[#EF9F27]"
                                  }`}
                                >
                                  {t}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    <div className="mt-4 flex gap-3">
                      <button
                        data-testid="booking-cancel-btn"
                        onClick={() => {
                          setBookingDoctor(null);
                          setRescheduleLocal(false);
                        }}
                        className="flex-1 rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-4 py-3 min-h-[44px]"
                      >
                        Back
                      </button>
                      <button
                        data-testid="booking-confirm-btn"
                        onClick={requestConsult}
                        disabled={
                          requesting ||
                          !bookDate ||
                          slotRequired(availability, bookDate, bookTime)
                        }
                        className="flex-1 rounded-full bg-[#EF9F27] text-[#111111] font-bold px-4 py-3 min-h-[44px] disabled:opacity-40"
                      >
                        {requesting ? "Requesting…" : "Confirm request"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Video review with the specialist */}
          <div className="mt-4">
            {c.video_consultation && !rescheduleVideo ? (
              <div className="bg-[#16A34A]/10 border border-[#16A34A]/30 rounded-2xl p-5" data-testid="case-video-consultation">
                <div className="flex items-center gap-2">
                  <Video size={18} className="text-[#22C55E]" />
                  <p className="font-head font-bold text-[#F8FAFC]">Video review scheduled</p>
                </div>
                <p className="text-sm text-[#A1A1AA] mt-1">
                  With {c.video_consultation.specialist?.name || sp.name}
                </p>
                <p className="text-sm text-[#F8FAFC] mt-2 inline-flex items-center gap-2" data-testid="case-video-when">
                  <CalendarDays size={15} className="text-[#22C55E]" />
                  {c.video_consultation.date}
                  {c.video_consultation.time ? ` at ${c.video_consultation.time}` : ""}
                </p>
                <p className="text-xs text-[#A1A1AA] mt-2">{c.video_consultation.join_link}</p>
                <div className="mt-4 flex gap-3">
                  <button
                    data-testid="video-reschedule-btn"
                    onClick={startRescheduleVideo}
                    className="flex-1 rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-4 py-2.5 min-h-[44px]"
                  >
                    Reschedule
                  </button>
                  <button
                    data-testid="video-cancel-booking-btn"
                    onClick={cancelVideo}
                    className="flex-1 rounded-full border border-[#EF4444]/50 text-[#EF4444] font-bold px-4 py-2.5 min-h-[44px] hover:bg-[#EF4444]/10 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {!rescheduleVideo && (
                  <button
                    data-testid="case-video-btn"
                    onClick={openVideoBooking}
                    className="w-full rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-6 py-3.5 min-h-[48px] flex items-center justify-center gap-2 hover:bg-[#333333] transition-colors"
                  >
                    <Video size={18} /> Book a video review with {sp.name?.split(" ").slice(-1)[0]}
                  </button>
                )}
                {(showVideo || rescheduleVideo) && (
                  <div className="mt-3 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5" data-testid="case-video-form">
                    <p className="font-head font-bold text-[#F8FAFC]">
                      {rescheduleVideo ? "Reschedule video review" : sp.name}
                    </p>
                    <p className="text-sm text-[#EF9F27]">{sp.specialty} · {sp.based}</p>
                    <BookingSlots
                      availability={videoAvail}
                      loading={loadingVideoAvail}
                      date={vDate}
                      time={vTime}
                      onDate={setVDate}
                      onTime={setVTime}
                      hint="Showing this specialist's video-review slots"
                      testidPrefix="video-booking"
                    />
                    <div className="mt-4 flex gap-3">
                      <button
                        data-testid="video-cancel-btn"
                        onClick={() => {
                          setShowVideo(false);
                          setRescheduleVideo(false);
                        }}
                        className="flex-1 rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-4 py-3 min-h-[44px]"
                      >
                        Back
                      </button>
                      <button
                        data-testid="video-confirm-btn"
                        onClick={scheduleVideo}
                        disabled={videoRequesting || !vDate || slotRequired(videoAvail, vDate, vTime)}
                        className="flex-1 rounded-full bg-[#EF9F27] text-[#111111] font-bold px-4 py-3 min-h-[44px] disabled:opacity-40"
                      >
                        {videoRequesting ? "Scheduling…" : "Confirm review"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Follow-up messaging */}
          <div className="mt-6" data-testid="case-followup">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquareText size={18} className="text-[#EF9F27]" />
              <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Follow-up questions</h2>
            </div>
            <p className="text-sm text-[#A1A1AA] mb-3">
              Ask your specialist a follow-up about this opinion.
            </p>

            {(c.messages || []).length > 0 && (
              <ul className="space-y-3 mb-4" data-testid="case-messages">
                {c.messages.map((m) => (
                  <li
                    key={m.id}
                    data-testid={`case-message-${m.id}`}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                        m.role === "user"
                          ? "bg-[#EF9F27] text-[#111111]"
                          : "bg-[#1A1A1A] border border-[#333333] text-[#E5E5E5]"
                      }`}
                    >
                      {m.role === "specialist" && (
                        <p className="text-xs font-bold text-[#EF9F27] mb-1">{m.author || "Specialist"}</p>
                      )}
                      <p className="text-sm leading-relaxed">{m.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex items-end gap-2">
              <textarea
                data-testid="case-message-input"
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                rows={2}
                placeholder="Type your follow-up question…"
                className="flex-1 bg-[#1A1A1A] border border-[#333333] rounded-xl px-4 py-3 text-[#F8FAFC] placeholder:text-[#666666] focus:outline-none focus:border-[#EF9F27] resize-none"
              />
              <button
                data-testid="case-message-send-btn"
                onClick={sendMessage}
                disabled={sending || !msgText.trim()}
                aria-label="Send follow-up"
                className="w-12 h-12 shrink-0 rounded-xl bg-[#EF9F27] text-[#111111] flex items-center justify-center transition-transform active:scale-95 disabled:opacity-40"
              >
                {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
              </button>
            </div>
          </div>
        </>
      )}

      <RecordPreviewDialog docId={previewDoc} open={!!previewDoc} onClose={() => setPreviewDoc(null)} />

      {isReady &&
        createPortal(
          <div className="case-print" data-testid="case-printable">
            <div style={{ maxWidth: 720, margin: "0 auto", padding: 32, fontFamily: "Georgia, serif", color: "#111" }}>
              <div style={{ borderBottom: "2px solid #111", paddingBottom: 12, marginBottom: 20 }}>
                <h1 style={{ margin: 0, fontSize: 22 }}>PocketDocta — Specialist Second Opinion</h1>
                <p style={{ margin: "4px 0 0", color: "#555" }}>
                  Prepared for discussion with your treating doctor
                </p>
              </div>
              <p><strong>Case:</strong> {c.title}</p>
              <p><strong>Specialty:</strong> {c.specialty}</p>
              <p><strong>Date:</strong> {c.created_at ? new Date(c.created_at).toLocaleString() : ""}</p>
              <p><strong>Reviewing specialist:</strong> {sp.name} — {sp.specialty}, {sp.based}</p>

              <h2 style={{ fontSize: 16, marginTop: 20 }}>Patient's concern</h2>
              <p>{c.concern}</p>

              <h2 style={{ fontSize: 16, marginTop: 20 }}>Summary</h2>
              <p>{op.summary}</p>

              <h2 style={{ fontSize: 16, marginTop: 20 }}>Key considerations</h2>
              <ul>{(op.considerations || []).map((x, i) => <li key={i}>{x}</li>)}</ul>

              <h2 style={{ fontSize: 16, marginTop: 20 }}>Questions to discuss</h2>
              <ul>{(op.suggested_questions || []).map((x, i) => <li key={i}>{x}</li>)}</ul>

              <h2 style={{ fontSize: 16, marginTop: 20 }}>Next steps</h2>
              <p>{op.next_steps}</p>

              {c.documents && c.documents.length > 0 && (
                <>
                  <h2 style={{ fontSize: 16, marginTop: 20 }}>Attached records</h2>
                  <ul>{c.documents.map((d) => <li key={d.id}>{d.label}</li>)}</ul>
                </>
              )}

              <p style={{ marginTop: 24, padding: 12, background: "#f3f3f3", borderRadius: 8, fontSize: 13, color: "#444" }}>
                {op.disclaimer}
              </p>
            </div>
          </div>,
          document.body
        )}
    </CareShell>
  );
}
